import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { NUMBER_FORMATS } from 'constants/interfaces';
import formatNumber from 'utils/formatNumber';
import formattedData from 'utils/formattedData';
import getNaceLabel from 'utils/getNaceLabel';

import type IStakeholderCustomerItem from 'store/slices/materialAssessment/interfaces/IStakeholderCustomerItem';
import type ICustomersTable from './interfaces/ICustomersTable';

import styles from './scss/CustomersTable.module.scss';

function CustomersTable({
  items, headerCells, isActionsVisible, isInfoTable, onDeleteItem,
  loadMoreCustomers, hasMore, naceCodeTreeData,
}: ICustomersTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');
  const cells = headerCells.filter(({ id }) => {
    if (id === 'actions' || id === 'action') {
      return isActionsVisible;
    }
    return true;
  });

  const cellsData = (item: IStakeholderCustomerItem) => ([
    { id: item.uniqueIdentifier, content: item.uniqueIdentifier || hyphen },
    { id: item.code, content: item.code || hyphen },
    { id: item.sourceVCMId, content: item.sourceVCMId || hyphen },
    { id: item.valueChainSection, content: item.valueChainSection || hyphen },
    { id: item.ownership, content: item.ownership || hyphen },
    { id: item.businessUnitType, content: item.businessUnitType || hyphen },
    {
      id: item.equityShareholding,
      content: formatNumber({ value: item.equityShareholding, type: NUMBER_FORMATS.percent }),
    },
    {
      id: item.financialControl,
      content: formatNumber({ value: item.financialControl, type: NUMBER_FORMATS.percent }),
    },
    { id: item.dateOpened, content: formattedData(item.dateOpened) || hyphen },
    { id: item.dateClosed, content: formattedData(item.dateClosed) || hyphen },
    { id: item.priorityRating, content: item.priorityRating || hyphen },
    {
      id: item.naceSection,
      content: getNaceLabel({ naceSection: item.naceSection, naceCodeTreeData }).naceSectionLabel || hyphen,
    },
    {
      id: item.naceCode,
      content: getNaceLabel({
        naceCode: item.naceCode,
        naceSection: item.naceSection,
        naceCodeTreeData,
      }).naceCodeLabel || hyphen,
    },
    { id: item.euTaxonomyCode, content: item.euTaxonomyCode || hyphen },

  ]);

  return (
    <div className={styles.root}>
      <InfiniteScroll
        dataLength={items?.length || 0}
        next={loadMoreCustomers}
        hasMore={hasMore}
        loader={false}
      >
        <div className={styles.tableBodyScroll}>
          <Table
            className={clsx(styles.table, {
              [styles.tableInfoTable]: isInfoTable,
            })}
          >
            <TableHead className={clsx(styles.header, {
              [styles.headerWithoutActions]: !isActionsVisible,
              [styles.headerInfoTable]: isInfoTable,
            })}
            >
              {cells.map(({
                id, label,
              }) => (
                <TableCell head className={styles.tableCell} key={id}>
                  <div>
                    <span>{t(label)}</span>
                  </div>
                </TableCell>
              ))}
            </TableHead>
            <TableBody className={clsx(styles.body, { [styles.bodyWithoutActions]: !isActionsVisible })}>
              {items ? items?.map((item, index) => (
                <TableRow key={[item.id, index].join('_')}>
                  <TableCell>
                    <div className={styles.staticCell}>
                      <span>{item.name}</span>
                    </div>
                  </TableCell>
                  {cellsData(item).map((cell, indexCell) => (
                    <TableCell key={[cell.id, indexCell].join('_')}>
                      {cell.content}
                    </TableCell>
                  ))}

                  <TableCell>
                    <div className={styles.profitCenterLinksContainer}>
                      <div className={styles.wrapper}>
                        {item.profitCenterLinks.slice(0, 3).map((profitCenterLink) => (
                          <div key={profitCenterLink.profitCenterId}>
                            {profitCenterLink.profitCenterId}
                          </div>
                        ))}
                        {item.profitCenterLinks.length > 3 && (
                        <div>...</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.addressCountry || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressState || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressCity || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressPostCode || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressStreet || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressBuilding || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressCoordinates || hyphen}
                  </TableCell>

                  {isActionsVisible ? (
                    <TableCell>
                      <IconButton
                        icon={<TrashIcon />}
                        onClick={() => onDeleteItem?.(item.id)}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              )) : null}
            </TableBody>
          </Table>
        </div>
      </InfiniteScroll>
    </div>
  );
}

export default CustomersTable;
